import { Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { BaryResult, BaryResultBuilder } from '../../../../models/bary-result';
import { FileUploader, FileUploadModule } from 'ng2-file-upload';
import { LocalStorageService } from 'ngx-webstorage';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaryComponentHelper } from '../../util/bary-component-helper';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ALLOWED_SHEET_FORMAT, DEFAULT_SIZE, ONE_MB } from '../../../../models/constants/file-upload.const';
import { BaryUploadUrlEnum } from '../../../../models/enums/bary-upload-url.enum';
import { environment } from '../../../../environments/environment';
import { MDBBootstrapModulesPro } from 'ng-uikit-pro-standard';
import { CommonModule } from '@angular/common';
import { BaryButtonComponent } from '../bary-button/bary-button.component';
import { TranslateDirective } from '../../../../directives/translate.directive';
import { NgxSpinnerService } from 'ngx-spinner';
import { BaryInputSelectComponent } from '../bary-input-select/bary-input-select.component';
import { DownloadFormat, LIST_DOWNLOAD_FORMAT } from '../../../../models/enums/download.format';
import { FaDirectiveEnum } from '../../../../models/enums/fa-directive.enum';
import { BaryIconComponent } from '../bary-icon/bary-icon.component';

@Component({
  selector: 'jhi-bary-file-upload',
  templateUrl: './bary-file-upload.component.html',
  styleUrls: ['./bary-file-upload.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    TranslateModule,
    MDBBootstrapModulesPro,
    FileUploadModule,
    ReactiveFormsModule,
    CommonModule,
    BaryButtonComponent,
    TranslateDirective,
    BaryInputSelectComponent,
    BaryIconComponent,
  ],
})
export class BaryFileUploadComponent implements OnInit, OnChanges {
  readonly spinnerService = inject(NgxSpinnerService);
  readonly localStorage = inject(LocalStorageService);
  readonly translateService = inject(TranslateService);

  @Input() public isTdb: boolean = true; // appears in table
  @Input() public hasHelp: boolean = true;
  @Input() public actionURL?: string | BaryUploadUrlEnum; // call direct en ajax
  @Input() public control: FormControl = new FormControl('');
  @Input() public inProcessing = false;
  @Input() public allowedFileType: string[] = ALLOWED_SHEET_FORMAT;
  @Input() public maxFileSize = DEFAULT_SIZE.maxFileSize; // default is 10Mb number
  @Input() public maxHeight = DEFAULT_SIZE.maxHeight;
  @Input() public maxWidth = DEFAULT_SIZE.maxWidth;
  @Input() public id = '';
  @Input() public downloadURL?: string;
  @Input() public downloadEmptyURL?: string;

  @Output() public resultEvent = new EventEmitter<BaryResult<any>>();
  @Output() public helpEvent = new EventEmitter<any>();
  @Output() public refreshEvent = new EventEmitter<undefined>();
  @Output() public deleteEvent = new EventEmitter<any>();

  @ViewChild('uploadFile') public uploadFile!: ElementRef;

  public uploader!: FileUploader;

  public errors: string[] = [];

  public downloadFormatControl = new FormControl(DownloadFormat.XLS);

  protected readonly FaDirectiveEnum = FaDirectiveEnum;

  protected readonly LIST_DOWNLOAD_FORMAT = LIST_DOWNLOAD_FORMAT;

  public ngOnInit(): void {
    if (!this.id) {
      this.id = BaryComponentHelper.makeId();
    }
    this.initFileUpload();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['inProcessing'] && changes['inProcessing'].currentValue === true) {
      this.control.disable({ onlySelf: true, emitEvent: false });
    } else {
      this.control.enable({ onlySelf: true, emitEvent: false });
    }
  }

  public checkAndUpload($event: File[]): void {
    this.checkFile($event);
    if (this.errors.length === 0) {
      this.uploader.uploadAll();
    }
  }

  public checkFile(files: File[]): void {
    this.errors = [];
    const file = files[0];
    if (file) {
      this.checkAllowedFile(file);
    } else {
      this.addError('File does not exist !');
    }
  }

  public download(url: string): void {
    this.spinnerService.show().then();
    const anchor = document.createElement('a');
    document.body.appendChild(anchor);

    const file = url.includes(environment.urlApi) ? url : environment.urlApi + url;
    const token = this.localStorage.retrieve('authenticationToken');
    const headers = new Headers();

    headers.append('Authorization', 'Bearer ' + token);

    const format = this.downloadFormatControl.value;

    fetch(file + '?format=' + format, { headers }).then(response => {
      response
        .blob()
        .then(blob => {
          const objectUrl = window.URL.createObjectURL(blob);
          const contentDisposition = response.headers.get('Content-Disposition') as string;
          const filename: string = (contentDisposition?.split(';')?.find(n => n.includes('filename=')) as string)
            ?.replace('filename=', '')
            ?.replace('"', '')
            ?.replace('"', '')
            ?.trim();

          anchor.href = objectUrl;
          anchor.download = filename ?? 'mon_fichier.xls';
          anchor.click();

          window.URL.revokeObjectURL(objectUrl);
        })
        .finally(() => this.spinnerService.hide());
    });
  }

  private initFileUpload(): void {
    if (this.actionURL) {
      const token = this.localStorage.retrieve('authenticationToken');

      this.uploader = new FileUploader({
        url: this.actionURL,
        itemAlias: 'uploadedFile',
        authToken: 'Bearer ' + token,
        maxFileSize: this.maxFileSize,
      });

      this.uploader.onBeforeUploadItem = (item: any) => {
        item.withCredentials = false;
      };
      this.uploader.onAfterAddingFile = file => {
        file.withCredentials = false;
      };
      this.uploader.onCompleteItem = (item: any, response: any, status: any) => {
        const parsedResponse = new BaryResultBuilder()
          .parseResponse(response)
          .success(status === 200)
          .build();

        this.resultEvent.emit(parsedResponse);
        this.uploader.clearQueue();
        this.uploadFile.nativeElement.value = ''; // reset l'affichage.
      };
    }
  }

  private checkAllowedFile(file: File): void {
    if (!this.allowedFileType.includes(file.name.substring(file.name.lastIndexOf('.') + 1))) {
      this.addError(
        this.translateService.instant('UPLOAD_FILE.ERROR.FORMAT_IS_NOT_VALID', {
          formatList: this.allowedFileType.toString(),
        })
      );
    }
    if (file.size > this.maxFileSize) {
      this.addError(
        this.translateService.instant('UPLOAD_FILE.ERROR.FILE_IS_TOO_BIG', {
          maxWeight: (this.maxFileSize / ONE_MB).toFixed(1),
        })
      );
    }
  }

  private addError(error: string): void {
    this.errors.push(error);
    setTimeout(() => {
      this.errors = [];
      this.uploadFile.nativeElement.value = '';
      this.uploader.clearQueue();
    }, 5000);
  }
}
